import { apiClient } from './api';

export interface WalletSummary {
  walletId: string;
  balance: number;
  currency: string;
  updatedAt: string;
}

export interface TopupTier {
  TierID: string;
  Name: string;
  PayAmount: number;
  CreditAmount: number;
  IsActive: boolean;
  SortOrder: number;
}

export interface WalletTransaction {
  TransactionID: string;
  WalletID: string;
  Type: 'topup' | 'debit' | 'adjustment' | 'refund';
  Amount: number;
  BalanceAfter: number;
  RefType: string | null;
  RefID: string | null;
  StripePaymentIntentID: string | null;
  TierID: string | null;
  Description: string | null;
  CreatedAt: string;
  CreatedBy: string | null;
}

class WalletService {
  async getMyWallet(): Promise<WalletSummary> {
    const r = await apiClient.get<WalletSummary>('/wallet');
    return r.data!;
  }

  async listTransactions(page = 1, limit = 20): Promise<{ items: WalletTransaction[]; pagination: any }> {
    const r = await apiClient.get<WalletTransaction[]>('/wallet/transactions', { page, limit });
    return { items: (r.data as any) ?? [], pagination: (r as any).pagination };
  }

  async listActiveTiers(): Promise<TopupTier[]> {
    const r = await apiClient.get<TopupTier[]>('/wallet/tiers');
    return r.data ?? [];
  }

  async createTopupOrder(tierId: string): Promise<{ orderId: string; keyId: string; amount: number; currency: string; tier: TopupTier }> {
    const r = await apiClient.post<{ orderId: string; keyId: string; amount: number; currency: string; tier: TopupTier }>(
      '/wallet/topup',
      { tierId }
    );
    return r.data!;
  }

  /** Verify a completed top-up and credit the wallet synchronously (no webhook needed). */
  async verifyTopup(payload: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }): Promise<{ balance: number; currency: string; updatedAt: string }> {
    const r = await apiClient.post<{ balance: number; currency: string; updatedAt: string }>(
      '/wallet/topup/verify',
      payload
    );
    return r.data!;
  }
}

class AdminWalletService {
  async listTiers(): Promise<TopupTier[]> {
    const r = await apiClient.get<TopupTier[]>('/admin/wallet/tiers');
    return r.data ?? [];
  }

  async createTier(input: { name: string; payAmount: number; creditAmount: number; isActive?: boolean; sortOrder?: number }): Promise<TopupTier> {
    const r = await apiClient.post<TopupTier>('/admin/wallet/tiers', input);
    return r.data!;
  }

  async updateTier(tierId: string, input: Partial<{ name: string; payAmount: number; creditAmount: number; isActive: boolean; sortOrder: number }>): Promise<TopupTier> {
    const r = await apiClient.put<TopupTier>(`/admin/wallet/tiers/${tierId}`, input);
    return r.data!;
  }

  async deactivateTier(tierId: string): Promise<void> {
    await apiClient.delete(`/admin/wallet/tiers/${tierId}`);
  }

  async listWallets(params: { page?: number; limit?: number; search?: string } = {}): Promise<{ items: any[]; pagination: any }> {
    const r = await apiClient.get<any[]>('/admin/wallet/wallets', params);
    return { items: (r.data as any) ?? [], pagination: (r as any).pagination };
  }

  async listUserTransactions(userId: string, page = 1, limit = 50): Promise<{ items: WalletTransaction[]; pagination: any; wallet: { walletId: string; balance: number; currency: string; updatedAt: string } | null }> {
    const r = await apiClient.get<WalletTransaction[]>(`/admin/wallet/wallets/${userId}/transactions`, { page, limit });
    return {
      items: (r.data as any) ?? [],
      pagination: (r as any).pagination,
      wallet: (r as any).wallet ?? null
    };
  }

  async adjustWallet(userId: string, amount: number, reason: string): Promise<{ balance: number; currency: string }> {
    const r = await apiClient.post<{ balance: number; currency: string }>(`/admin/wallet/wallets/${userId}/adjust`, { amount, reason });
    return r.data!;
  }
}

export const walletService = new WalletService();
export const adminWalletService = new AdminWalletService();
